import api from './api';
import { ApiResponse, Teacher, TeacherRequest, TeacherPermission, PageResponse, TeacherSchoolAssignment, AssignmentRequest } from '../types';

export const teacherService = {
  getTeachers: async (params: {
    schoolId: number;
    search?: string;
    page?: number;
    size?: number;
    sort?: string;
  }): Promise<PageResponse<Teacher>> => {
    const response = await api.get<ApiResponse<PageResponse<Teacher>>>('/admin/teachers', {
      params: {
        schoolId: params.schoolId,
        search: params.search,
        page: params.page || 0,
        size: params.size || 20,
        sort: params.sort || 'name,asc',
      },
    });
    return response.data.data;
  },

  getTeacher: async (id: number): Promise<Teacher> => {
    const response = await api.get<ApiResponse<Teacher>>(`/admin/teachers/${id}`);
    return response.data.data;
  },

  createTeacher: async (data: TeacherRequest): Promise<Teacher> => {
    const response = await api.post<ApiResponse<Teacher>>('/admin/teachers', data);
    return response.data.data;
  },

  updateTeacher: async (id: number, data: TeacherRequest): Promise<Teacher> => {
    const response = await api.put<ApiResponse<Teacher>>(`/admin/teachers/${id}`, data);
    return response.data.data;
  },

  deleteTeacher: async (id: number): Promise<void> => {
    await api.delete(`/admin/teachers/${id}`);
  },

  resetPassword: async (id: number): Promise<void> => {
    await api.post(`/admin/teachers/${id}/reset-password`);
  },

  getPermissions: async (id: number): Promise<TeacherPermission> => {
    const response = await api.get<ApiResponse<TeacherPermission>>(`/admin/teachers/${id}/permissions`);
    return response.data.data;
  },

  updatePermissions: async (id: number, data: TeacherPermission): Promise<TeacherPermission> => {
    const response = await api.put<ApiResponse<TeacherPermission>>(`/admin/teachers/${id}/permissions`, data);
    return response.data.data;
  },

  // 获取教师任职历史
  getAssignments: async (id: number): Promise<TeacherSchoolAssignment[]> => {
    const response = await api.get<ApiResponse<TeacherSchoolAssignment[]>>(`/admin/teachers/${id}/assignments`);
    return response.data.data;
  },

  // 新增教师任职记录
  addAssignment: async (id: number, data: AssignmentRequest): Promise<TeacherSchoolAssignment> => {
    const response = await api.post<ApiResponse<TeacherSchoolAssignment>>(`/admin/teachers/${id}/assignments`, data);
    return response.data.data;
  },

  /**
   * 批量导入老师 (Excel)
   */
  importTeachers: async (schoolId: number, file: File): Promise<TeacherImportResult> => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await api.post<ApiResponse<TeacherImportResult>>(
      '/admin/teachers/import',
      fd,
      {
        params: { schoolId },
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return r.data.data;
  },

  /**
   * 下载老师批量导入模板（Excel）
   */
  downloadImportTemplate: async (): Promise<void> => {
    const r = await api.get<Blob>('/admin/teachers/import-template', {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = '老师导入模板.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

export interface TeacherImportResult {
  totalRows: number;
  success: number;
  skipped: number;
  failed: number;
  details: Array<{
    rowNumber: number;
    idCardNumber?: string;
    name?: string;
    employeeNumber?: string;
    department?: string;
    status: 'PENDING' | 'SUCCESS' | 'SKIPPED' | 'FAILED';
    errorMessage?: string;
  }>;
}
